import React, { memo } from 'react';
import { WidgetType } from '../../types/api/ElementType';
import { AppLibraryWidget } from '../../_widgets/AppLibrary';
import { AttachedFilesWidget } from '../../_widgets/AttachedFiles';
import {
  AnnouncementCategoriesWidget,
  FileCategoriesWidget,
  PageTypeCategoriesWidget,
} from '../../_widgets/Categories';
import { PageTypeValidityPeriodWidget } from '../../_widgets/PageTypeValidityPeriod';
import { PageToPageWidget } from '../../_widgets/PageToPage';
import { StructuralPageToPageWidget } from '../../_widgets/StructuralPageToPage';
import { BlogAuthorsWidget } from '../../_widgets/BlogAuthors';
import { BlogPostSiblingsWidget } from '../../_widgets/BlogPostSiblings';
import { LatestAdditionsWidget } from '../../_widgets/LatestAdditions';
import { UpcomingEventsWidget } from '../../_widgets/UpcomingEvents';
import { PageSettingsWidget } from '../../_widgets/PageSettings';
import { AnnouncementsWidget } from '../../_widgets/Announcements';
import { ExternalLinksWidget } from '../../_widgets/ExternalLinks';
import { ContactPersonsWidget } from '../../_widgets/ContactPersons';
import { PortalSettingImportExportWidget } from '../../_widgets/PortalSettingImportExport';
import { TeamRoomMemberRequestListWidget } from '../../_widgets/TeamRoomMemberRequestsList';
import { GenericSearchListWidget } from '../../_widgets/GenericSearchList';
import { StatusInfoWarning } from '../../_lib/components/StatusInfo';
import { AreaGroupsWidget } from '../../_widgets/AreaGroups';
import { AttributeGroupInfoWidget } from '../../_widgets/AttributeGroupInfo';
import { RoleSettingsWidget } from '../../_widgets/RoleSettings';
import { PageMembershipMatchingRuleSettingsWidget } from '../../_widgets/PageMembershipMatchingRuleSettings';
import { RssFeedWidget } from '../../_widgets/RssFeed';
import bem from '../../lib/bemUtils/bem';
import { AttributeGroupActivityWidget } from '../../_widgets/AttributeGroupActivity';
import { LatestForumPostsWidget } from '../../_widgets/LatestForumPosts/LatestForumPostsWidget';
import { CalendarWidget } from '../../_widgets/Calendar/CalendarWidget';
import { PrivilegedGroupsWidget } from '../../_widgets/PrivilegedGroups';
import { PendingAccessLevelRequestWidget } from '../../_widgets/fib/PendingAccessLevelRequestWidget';
import { EditorFeedbackWidget } from '../../_widgets/EditorFeedback';
import { PageMembershipMatchingAttributesWidget } from '../../_widgets/PageMembershipMatchingAttributes';
import { UserImportConfigurationWidget } from '../../_widgets/UserImportConfiguration/UserImportConfigurationWidget';

type ContextSectionWidgets =
  | AppLibraryWidget
  | AttachedFilesWidget
  | FileCategoriesWidget
  | PageTypeCategoriesWidget
  | AnnouncementCategoriesWidget
  | PageTypeValidityPeriodWidget
  | PageToPageWidget
  | StructuralPageToPageWidget
  | BlogAuthorsWidget
  | BlogPostSiblingsWidget
  | LatestAdditionsWidget
  | UpcomingEventsWidget
  | PageSettingsWidget
  | ExternalLinksWidget
  | AnnouncementsWidget
  | ContactPersonsWidget
  | PortalSettingImportExportWidget
  | TeamRoomMemberRequestListWidget
  | AreaGroupsWidget
  | GenericSearchListWidget
  | RoleSettingsWidget
  | PageMembershipMatchingRuleSettingsWidget
  | AttributeGroupInfoWidget
  | RssFeedWidget
  | AttributeGroupActivityWidget
  | CalendarWidget
  | LatestForumPostsWidget
  | PrivilegedGroupsWidget
  | PageMembershipMatchingAttributesWidget
  | EditorFeedbackWidget
  | PendingAccessLevelRequestWidget
  | UserImportConfigurationWidget;

export type ContextSection = {
  widgets: ContextSectionWidgets[];
  uuid: string;
  modifier?: string;
};

export const getContextSectionWidget = (widget: ContextSectionWidgets) => {
  switch (widget.elementType) {
    case WidgetType.applibrary:
    case WidgetType.applibraryglobal:
      return <AppLibraryWidget key={widget.uuid} {...widget} />;
    case WidgetType.attachedfiles:
      return <AttachedFilesWidget key={widget.uuid} {...widget} />;
    case WidgetType.attributegroupinfo:
      return <AttributeGroupInfoWidget key={widget.uuid} {...widget} />;
    case WidgetType.blogauthors:
      return <BlogAuthorsWidget key={widget.uuid} {...widget} />;
    case WidgetType.blogpostsiblings:
      return <BlogPostSiblingsWidget key={widget.uuid} {...widget} />;
    case WidgetType.calendar:
      return <CalendarWidget key={widget.uuid} {...widget} />;
    case WidgetType.contactpersons:
      return <ContactPersonsWidget key={widget.uuid} {...widget} />;
    case WidgetType.currentannouncementsinbox:
    case WidgetType.currentannouncementsoutbox:
    case WidgetType.currentmandatoryannouncementsforpage:
      return <AnnouncementsWidget key={widget.uuid} {...widget} />;
    case WidgetType.editorFeedback:
      return <EditorFeedbackWidget key={widget.uuid} {...widget} />;
    case WidgetType.externallinks:
      return <ExternalLinksWidget key={widget.uuid} {...widget} />;
    case WidgetType.genericsearchlist:
      return <GenericSearchListWidget key={widget.uuid} {...widget} />;
    case WidgetType.latestadditions:
      return <LatestAdditionsWidget key={widget.uuid} {...widget} />;
    case WidgetType.pageToPage:
      return <PageToPageWidget key={widget.uuid} {...widget} />;
    case WidgetType.pagesettings:
      return <PageSettingsWidget key={widget.uuid} {...widget} />;
    case WidgetType.announcementcategories:
      return <AnnouncementCategoriesWidget key={widget.uuid} {...widget} />;
    case WidgetType.pagetypecategories:
      return <PageTypeCategoriesWidget key={widget.uuid} {...widget} />;
    case WidgetType.pagetypevalidityperiod:
      return <PageTypeValidityPeriodWidget key={widget.uuid} {...widget} />;
    case WidgetType.portalsettingimportexport:
      return <PortalSettingImportExportWidget key={widget.uuid} {...widget} />;
    case WidgetType.structuralPageToPage:
      return <StructuralPageToPageWidget key={widget.uuid} {...widget} />;
    case WidgetType.teamroommemberrequestslist:
      return <TeamRoomMemberRequestListWidget key={widget.uuid} {...widget} />;
    case WidgetType.areagroups:
      return <AreaGroupsWidget key={widget.uuid} {...widget} />;
    case WidgetType.upcomingevents:
      return <UpcomingEventsWidget key={widget.uuid} {...widget} />;
    case WidgetType.rolesettings:
      return <RoleSettingsWidget key={widget.uuid} {...widget} />;
    case WidgetType.rssfeed:
      return <RssFeedWidget key={widget.uuid} {...widget} />;
    case WidgetType.attributegroupactivity:
      return <AttributeGroupActivityWidget key={widget.uuid} {...widget} />;
    case WidgetType.filecategories:
      return <FileCategoriesWidget key={widget.uuid} {...widget} />;
    case WidgetType.latestforumposts:
      return <LatestForumPostsWidget key={widget.uuid} {...widget} />;
    case WidgetType.privilegedgroups:
      return <PrivilegedGroupsWidget key={widget.uuid} {...widget} />;
    case WidgetType.pagemembershipmatchingattributes:
      return <PageMembershipMatchingAttributesWidget key={widget.uuid} {...widget} />;
    case WidgetType.pagemembershipmatchingrulesettings:
      return <PageMembershipMatchingRuleSettingsWidget key={widget.uuid} {...widget} />;
    case WidgetType.pendingAccessLevelRequests:
      return <PendingAccessLevelRequestWidget key={widget.uuid} {...widget} />;
    case WidgetType.userimportconfigurationwidget:
      return <UserImportConfigurationWidget key={widget.uuid} {...widget} />;
    default:
      return <StatusInfoWarning modifier="padding" />;
  }
};

/**
 * Memorized ContextSection
 * - only rerender when section uuid changes
 * - helps avoid rerender when global context provider change or navigating via spa
 */
export const ContextSection = memo(
  function ({ widgets, modifier, uuid: _ }: ContextSection) {
    ContextSection.displayName = 'ContextSection';

    const newWidgets = [...widgets];
    return (
      newWidgets && (
        <div className={bem('content-column-one-third', modifier)}>{newWidgets.map(getContextSectionWidget)}</div>
      )
    );
  },
  (prevProps, nextProps) => prevProps.uuid === nextProps.uuid,
);
